import { formatLoose } from './format';

/**
 * **Did these two people ever actually stand in the same room?**
 *
 * The Network tab draws a line from the subject to an org and another from that
 * org to someone else, and the picture invites a reading it has not earned: a
 * two-year stint in the 2010s is not a connection to someone who played there
 * in the nineties. This module is the arithmetic that says so — given the
 * windows on the subject's ties to a node and the windows on the other person's
 * ties to the same node, does the record hold a stretch of time both were
 * there?
 *
 * It answers in three states, and the third one is the point: **unknown** is
 * not **none**. A record with no dates on a board seat has not said the two
 * never met; it has said nothing. Collapsing that into "no overlap" would turn
 * a gap in the sources into a finding, which is the one thing this view must
 * never do.
 *
 * **The rules, exactly as implemented.**
 *  - A side with no windows at all, or whose every window has both bounds null,
 *    is `unknown`. One dated window on a side is enough to make that side
 *    answerable; the undated ones on it are then ignored, because a window that
 *    says nothing cannot narrow or widen anything.
 *  - A null bound is unbounded, not missing: `start_date: null` reads "since
 *    before the record starts", `end_date: null` reads "still current". That is
 *    the wire's own meaning (shared/src/network.ts), so it is honoured rather
 *    than reinterpreted.
 *  - Each side is the *union* of its windows — several stints at one org are
 *    several chances to have overlapped, and any one of them counts.
 *  - The two unions intersect if any window of one meets any window of the
 *    other. Meeting is inclusive at the day: a seat ending on the day another
 *    begins is an overlap, because the grain here is a calendar day and both
 *    were there on it.
 *  - The reported bounds are the intersection itself, tightened to the later
 *    start and the earlier end of the pair that met, and null where that edge
 *    is unbounded on both sides. Where several pairs meet, the intersections
 *    that touch — sharing a day, or sitting on consecutive ones, which is how a
 *    record stores one continuous tie it happened to write down twice — are
 *    merged, and **every** interval that survives the merge is reported, in
 *    chronological order. Two stints that overlapped in the seventies and again
 *    in the nineties are two overlaps: a single span covering both would print
 *    the twenty years between them as though the tie had held throughout, and
 *    printing only the first would hide the second — the later stretch is
 *    usually the one a reader is asking about.
 *
 * **Loose bounds, and why the arithmetic and the sentence disagree on purpose.**
 * A window bound carries the precision its source stated (`LooseDate` —
 * shared/src/common.ts), so `2021`, `2021-05` and `2021-05-04` all arrive here.
 * Comparing them needs a full day, so each bound is normalised to one *for
 * comparison only*: a **start** expands to the first day of its period and an
 * **end** to the last (`2021` is 2021-01-01…2021-12-31, `2020-02` ends on the
 * 29th). That is the only reading of a year that does not invent a fact — a
 * seat held "in 2021" was held for some of 2021, and the widest reading is the
 * one that refuses to rule an overlap out.
 *
 * What gets **reported** is never the normalised day. Each interval keeps the
 * stated text of whichever bound won the comparison, so a year that survives as
 * the later start is printed as a year: "Overlap: since 2021", not "since Jan
 * 2021". Printing the normalised value would put the fabricated month back, one
 * layer down from where it was just removed.
 *
 * Normalised dates are `YYYY-MM-DD` strings and are compared as strings.
 * Zero-padded fixed-width fields make lexical order calendar order, so the
 * comparisons themselves need no parsing, no timezone, and no `Date` object.
 */

/** One stretch of time, as the wire sends it. Null is "no dates on record". */
export type Window = { start_date: string | null; end_date: string | null } | null;

/**
 * One stretch both were there for. Null is unbounded, exactly as on a window,
 * and a bound that is present is in the words the source used for it — a year
 * stays a year.
 */
export type Interval = { start: string | null; end: string | null };

export type Overlap =
  { kind: 'overlap'; intervals: Interval[] } | { kind: 'none' } | { kind: 'unknown' };

/**
 * A bound as both things it has to be: `text` is what the source said and what
 * gets printed; `key` is the full day it is compared on. They are carried
 * together so that a comparison can never accidentally become a render.
 */
type Bound = { text: string; key: string };

/** A window that has at least one bound — the only kind worth intersecting. */
type Dated = { start: Bound | null; end: Bound | null };

/** The pieces of a `YYYY`, `YYYY-MM` or `YYYY-MM-DD`, or null for anything else. */
function partsOf(value: string): { year: number; month: number | null; day: number | null } | null {
  const match = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/.exec(value);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: match[2] ? Number(match[2]) : null,
    day: match[3] ? Number(match[3]) : null,
  };
}

const pad = (value: number): string => String(value).padStart(2, '0');

/**
 * The last day of a month, as the calendar has it — February included, leap
 * years included. `Date.UTC` with day 0 of the *next* month is the calendar's
 * own answer; it is used as a calendar and never as an instant, so no timezone
 * reaches it.
 */
const lastDayOfMonth = (year: number, month: number): number =>
  new Date(Date.UTC(year, month, 0)).getUTCDate();

/** A start bound opens at the first day of whatever period it names. */
function startBound(value: string | null): Bound | null {
  if (value === null) return null;
  const parts = partsOf(value);
  if (!parts) return { text: value, key: value };
  const month = parts.month ?? 1;
  const day = parts.day ?? 1;
  return { text: value, key: `${parts.year}-${pad(month)}-${pad(day)}` };
}

/** An end bound closes on the last day of whatever period it names. */
function endBound(value: string | null): Bound | null {
  if (value === null) return null;
  const parts = partsOf(value);
  if (!parts) return { text: value, key: value };
  const month = parts.month ?? 12;
  const day = parts.day ?? lastDayOfMonth(parts.year, month);
  return { text: value, key: `${parts.year}-${pad(month)}-${pad(day)}` };
}

function datedOf(windows: readonly Window[]): Dated[] {
  const kept: Dated[] = [];
  for (const window of windows) {
    if (!window) continue;
    if (window.start_date === null && window.end_date === null) continue;
    kept.push({ start: startBound(window.start_date), end: endBound(window.end_date) });
  }
  return kept;
}

/** The later of two starts; null on both sides is unbounded and stays null. */
const laterStart = (a: Bound | null, b: Bound | null): Bound | null =>
  a === null ? b : b === null ? a : a.key > b.key ? a : b;

/** The earlier of two ends, with null unbounded the same way. */
const earlierEnd = (a: Bound | null, b: Bound | null): Bound | null =>
  a === null ? b : b === null ? a : a.key < b.key ? a : b;

/**
 * The two open bounds, as sortable strings. `''` precedes every `YYYY-…` and
 * `'￿'` follows every one, so an unbounded edge orders correctly without a
 * null check at each comparison. Only ever used for ordering — the value that
 * gets printed is the bound's own text.
 */
const asStart = (bound: Bound | null): string => bound?.key ?? '';
const asEnd = (bound: Bound | null): string => bound?.key ?? '￿';

/**
 * The calendar day after a `YYYY-MM-DD`, so that two stints stored back to back
 * ("… to 2014-12-31", "2015-01-01 to …") are seen to be continuous. Month
 * lengths and leap years are why this is arithmetic rather than a string edit;
 * `Date.UTC` is used purely as the calendar, never as an instant, so no
 * timezone reaches it. It is only ever handed a normalised key, so a loose
 * bound reaches it as the day its period actually closed on.
 */
function dayAfter(day: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return day;
  const at = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + 1));
  return at.toISOString().slice(0, 10);
}

/** Non-empty at day grain: an interval whose start is after its end is not one. */
const meets = (start: Bound | null, end: Bound | null): boolean =>
  start === null || end === null || start.key <= end.key;

/** See the module docstring — the rules are stated there in full. */
export function overlapOf(a: readonly Window[], b: readonly Window[]): Overlap {
  const left = datedOf(a);
  const right = datedOf(b);
  if (left.length === 0 || right.length === 0) return { kind: 'unknown' };

  const met: Dated[] = [];
  for (const one of left) {
    for (const two of right) {
      const start = laterStart(one.start, two.start);
      const end = earlierEnd(one.end, two.end);
      if (meets(start, end)) met.push({ start, end });
    }
  }
  if (met.length === 0) return { kind: 'none' };

  // Plain string comparison, not `localeCompare`: these are fixed-width digits
  // and the order must be the same wherever the test runs.
  met.sort((one, two) => {
    if (asStart(one.start) !== asStart(two.start)) {
      return asStart(one.start) < asStart(two.start) ? -1 : 1;
    }
    if (asEnd(one.end) === asEnd(two.end)) return 0;
    return asEnd(one.end) < asEnd(two.end) ? -1 : 1;
  });

  // Fold each interval into the one before it where the two touch, and open a
  // new one where they do not: a gap between two intersections is a stretch the
  // record does *not* put both of them there for, and closing over it would be
  // the overstatement this module exists to refuse.
  const merged: Dated[] = [];
  for (const next of met) {
    const last = merged[merged.length - 1];
    if (!last || (last.end !== null && asStart(next.start) > dayAfter(last.end.key))) {
      merged.push({ start: next.start, end: next.end });
      continue;
    }
    // An open end swallows everything after it; otherwise the later close wins.
    if (asEnd(next.end) > asEnd(last.end)) last.end = next.end;
  }

  // Only here does a bound stop being a comparison and become a reading: each
  // survivor is reported in the words of whichever bound won.
  const intervals: Interval[] = merged.map((one) => ({
    start: one.start?.text ?? null,
    end: one.end?.text ?? null,
  }));
  return { kind: 'overlap', intervals };
}

/** One stretch, said the way a window is said. Null when it has no edges. */
function intervalText(interval: Interval): string | null {
  const { start, end } = interval;
  if (start && end) return `${formatLoose(start)} to ${formatLoose(end)}`;
  if (start) return `since ${formatLoose(start)}`;
  if (end) return `until ${formatLoose(end)}`;
  return null;
}

/** "a", "a, and b", "a, b, and c" — the list as a reader would say it aloud. */
function listed(parts: readonly string[]): string {
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

/**
 * The overlap as the line the panel prints.
 *
 * "Dates not on record" rather than "unknown" or "no overlap": the absence is
 * in the sources, and the sentence has to say which of the two things it means.
 *
 * Every stretch is named, not just the first: "Overlap: 1994 to 1995, and 2011"
 * is two separate times these two were in the same place, and a line that
 * stopped at the first would quietly hide the recent one.
 */
export function overlapText(overlap: Overlap): string {
  if (overlap.kind === 'unknown') return 'Dates not on record';
  if (overlap.kind === 'none') return 'No overlap on record';
  const parts = overlap.intervals.map(intervalText).filter((part): part is string => part !== null);
  // Both sides open at both ends: they were there together, and the record
  // holds no edge of it to print.
  if (parts.length === 0) return 'Overlap on record';
  return `Overlap: ${listed(parts)}`;
}
